import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * Native production builds can reach the backend through two origins:
 * the global one (EXPO_PUBLIC_API_URL, Google-hosted) and a China-reachable
 * edge (EXPO_PUBLIC_API_URL_CN). The choice is made once at boot, before any
 * module that captures HOST is loaded, and re-validated in the background for
 * the next launch — never switched mid-session (cookies are per-origin).
 */

const STORAGE_KEY = 'smartsaas_api_origin_choice_v1';
const PROBE_TIMEOUT_MS = 2500;
const ORIGIN_GLOBAL_KEY = '__SMARTSAAS_API_ORIGIN__';

const normalizeOrigin = (value?: string | null): string | undefined => {
	if (!value) return undefined;
	let v = value.trim();
	if (
		(v.startsWith("'") && v.endsWith("'")) ||
		(v.startsWith('"') && v.endsWith('"'))
	) {
		v = v.slice(1, -1).trim();
	}
	v = v.replace(/\/+$/, '');
	return v || undefined;
};

/** Web hostnames served through the China edge; pages loaded there must call the China API origin. */
const CHINA_WEB_HOSTNAMES = ['cn.smartsaas.pro'];

const getChinaWebApiOrigin = (): string | undefined => {
	const hostname = typeof window !== 'undefined' ? window.location?.hostname : undefined;
	if (!hostname || !CHINA_WEB_HOSTNAMES.includes(hostname)) return undefined;
	return normalizeOrigin(process.env.EXPO_PUBLIC_API_URL_CN);
};

export const getApiOriginOverride = (): string | undefined =>
	normalizeOrigin((globalThis as Record<string, unknown>)[ORIGIN_GLOBAL_KEY] as string | undefined) ??
	getChinaWebApiOrigin();

const setApiOriginOverride = (origin: string) => {
	(globalThis as Record<string, unknown>)[ORIGIN_GLOBAL_KEY] = origin;
};

const probeOrigin = async (origin: string, timeoutMs: number): Promise<boolean> => {
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), timeoutMs);
	try {
		const res = await fetch(`${origin}/ping`, {
			method: 'GET',
			cache: 'no-store',
			signal: controller.signal,
		});
		return res.ok;
	} catch {
		return false;
	} finally {
		clearTimeout(timer);
	}
};

/** Prefer the global origin; fall back to the China edge only if global is unreachable. */
export const pickReachableOrigin = async (
	primary: string,
	fallback: string,
	probe: (origin: string, timeoutMs: number) => Promise<boolean> = probeOrigin,
	timeoutMs: number = PROBE_TIMEOUT_MS
): Promise<string> => {
	const fallbackProbe = probe(fallback, timeoutMs);
	if (await probe(primary, timeoutMs)) return primary;
	if (await fallbackProbe) return fallback;
	return primary;
};

const readCachedOrigin = async (allowed: string[]): Promise<string | undefined> => {
	try {
		const cached = normalizeOrigin(await AsyncStorage.getItem(STORAGE_KEY));
		return cached && allowed.includes(cached) ? cached : undefined;
	} catch {
		return undefined;
	}
};

const saveOrigin = async (origin: string) => {
	try {
		await AsyncStorage.setItem(STORAGE_KEY, origin);
	} catch {
		/* best effort */
	}
};

/**
 * Resolve which API origin this launch uses. No-op in dev or when no China
 * origin is configured, so HOST keeps its existing behaviour.
 */
export const resolveApiOrigin = async (): Promise<void> => {
	if (__DEV__) return;
	const primary = normalizeOrigin(process.env.EXPO_PUBLIC_API_URL);
	const china = normalizeOrigin(process.env.EXPO_PUBLIC_API_URL_CN);
	if (!primary || !china || primary === china) return;

	const cached = await readCachedOrigin([primary, china]);
	if (cached) {
		setApiOriginOverride(cached);
		void pickReachableOrigin(primary, china).then(saveOrigin);
		return;
	}

	const chosen = await pickReachableOrigin(primary, china);
	setApiOriginOverride(chosen);
	await saveOrigin(chosen);
};
